import { AxiosResponse } from "axios";
import jwt_decode from "jwt-decode";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  LoginResponseBodyType,
  LoginResponseBodyUserType,
} from "@intelligems/ig-types";
import { useAppLaunchedCallback } from "@src/hooks/appLaunch";
import { GENERAL_ROUTES } from "@src/routes/routes";
import { setUser } from "@src/utils/setUser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useQueryParam } from "../../hooks/util";
import { QueryMeta } from "../../types/react-query";
import { getAuthToken, Logout } from "../../utils";
import { IgStorage } from "../../utils/igStorage";
import { api } from "../api";
import { useGetOrg } from "../organizations/get-organization";
import { userKeys } from "../queryKeys";

const login = async (data: { email: string; password: string }) => {
  const resp = await api.post<LoginResponseBodyType>(`/v2/login`, { ...data });
  return resp.data;
};

export const useRefreshShopifyScopes = () => {
  const token = getAuthToken();
  const org = useGetOrg();

  return useMutation<LoginResponseBodyType, Error, any, any>({
    mutationFn: async () =>
      JwtLogin({ token: token!, orgId: org.data.org.id, reAuth: true }),
    onSuccess(data) {
      if (data.type === "redirect") {
        open(data.redirect, "_blank");
      }
    },
  });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const widgetAuth = useQueryParam("widgetAuth");

  return useMutation<LoginResponseBodyType, Error, any, any>({
    mutationFn: async (data: { email: string; password: string }) =>
      login(data),
    onSuccess: (data) => {
      if (data.type === "redirect") {
        void navigate(data.redirect);
      } else if (data.type === "clear") {
        IgStorage.clearAuthTokens();
        window.location.reload();
      } else {
        if (
          window.location.pathname === "/launch" &&
          !data.organization.installed
        ) {
          IgStorage.clearAuthTokens();
        } else if (widgetAuth) {
          try {
            // eslint-disable-next-line react-compiler/react-compiler
            IgStorage.currentToken = data.token;
          } catch (e) {
            console.warn(e);
          }
        } else {
          IgStorage.authToken = data.token;
          queryClient.setQueryData(userKeys.all, data);
          setUser(data);
        }
      }
    },
  });
};

export const JwtLogin = async (options: {
  token: string;
  orgId?: string;
  shopId?: string;
  reAuth?: boolean;
  returnUrl?: string;
}): Promise<LoginResponseBodyType> => {
  const decodedToken = jwt_decode(options.token);
  let resp: AxiosResponse<LoginResponseBodyType>;
  // @ts-ignore
  if (!options.orgId || decodedToken.orgId === options.orgId) {
    resp = await api.post<LoginResponseBodyType>("/v2/login", {
      token: options.token,
      reAuth: options.reAuth,
      returnUrl: options.returnUrl,
    });
  } else {
    resp = await api.post<LoginResponseBodyType>("/v2/login", {
      token: options.token,
      orgId: options.orgId,
      shopId: options.shopId,
      reAuth: options.reAuth,
      returnUrl: options.returnUrl,
    });
  }
  return resp.data;
};

export const useLoggedInUser = () => {
  const urlJWT = getAuthToken();
  return useQuery<LoginResponseBodyType, Error, LoginResponseBodyUserType, any>(
    {
      queryKey: userKeys.all,
      queryFn: async () =>
        JwtLogin({
          token: urlJWT!,
        }),
      enabled: window.location.pathname !== "/launch" && !!urlJWT,
    }
  );
};

export const useUser = (options?: { enabled?: boolean }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const appLaunchedCallback = useAppLaunchedCallback();
  const queryClient = useQueryClient();
  let isLaunchWithWrongOrg = false;
  const widgetAuth = searchParams.get("widgetAuth");
  // org id is out of sync with token, so the other store is requested
  const urlJWT = getAuthToken();
  let orgId: string | undefined = undefined;
  if (urlJWT) {
    const decoded = jwt_decode<any>(urlJWT);
    orgId = decoded.orgId;
    if (
      window.location.pathname === "/launch" &&
      decoded.shopId !== searchParams.get("shop")
    ) {
      isLaunchWithWrongOrg = true;
    }
  }
  const url = new URL(window.location.href);
  const returnUrl = url.searchParams.get("returnUrl") ?? url.pathname;

  return useQuery<LoginResponseBodyType, Error, LoginResponseBodyType, any>({
    queryKey: userKeys.all,
    queryFn: async () =>
      JwtLogin({
        token: urlJWT!,
        orgId: widgetAuth || orgId || undefined,
        shopId:
          searchParams.get("shop") || searchParams.get("shopId") || undefined,
        returnUrl,
      }),
    enabled:
      !isLaunchWithWrongOrg &&
      ![null, undefined, "undefined"].includes(urlJWT) &&
      (options?.enabled === undefined || options?.enabled === true),
    retry: 3,
    meta: {
      onSuccess: (data: LoginResponseBodyType) => {
        if (data.type === "redirect") {
          // eslint-disable-next-line react-compiler/react-compiler
          window.location.href = data.redirect;
        } else if (data.type === "clear") {
          IgStorage.clearAuthTokens();
          window.location.reload();
        } else {
          if (window.location.pathname === "/launch") {
            if (data.organization.installed) {
              window.location.pathname = GENERAL_ROUTES.Index;
            } else {
              appLaunchedCallback.mutate();
              IgStorage.clearAuthTokens();
            }
          } else {
            IgStorage.authToken = data.token;
            setUser(data);
          }
        }
      },
      onError: {
        any: () => {
          if (!searchParams.has("widgetAuth")) {
            queryClient.removeQueries({ queryKey: userKeys.all });
            Logout();
          }
        },
      },
    } as QueryMeta,
  });
};
